import classNames from "classnames";
import React, {
  CSSProperties,
  FC,
  KeyboardEvent,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { LuChevronDown } from "react-icons/lu";

import { Input } from "../input";
import {
  SELECT_DEFAULT_MENU_HEIGHT,
  SELECT_LOAD_MORE_THRESHOLD,
  SELECT_MENU_OFFSET,
  SELECT_SIZE_CLASSES,
} from "./Select.constants";
import {
  getCssSize,
  getNextMultiValue,
  getSelectedOptions,
  getVirtualConfig,
  isSameOption,
  useOutsideClick,
} from "./Select.helpers";
import { SelectSpinner, SelectValueView } from "./Select.parts";
import { SelectOption, SelectProps } from "./Select.types";
import { SelectMenu } from "./SelectMenu";

export const Select: FC<SelectProps> = ({
  options,
  value = null,
  onChange,
  isMulti,
  isMulty,
  selectionMode = "immediate",
  loading,
  loadingMore,
  disabled,
  error,
  dropMenuVariant = "content",
  menuBehavior = "dropdown",
  icon,
  placeholder = "Select",
  emptyLabel = "No options found",
  emptyState,
  size = "md",
  clearOnClose = false,
  closeOnSelect,
  tabs,
  activeTab,
  onTabChange,
  createAction,
  search,
  selectAll,
  listHeader,
  footer,
  styleConfig,
  dropMenuMaxHeight,
  hasMore,
  onLoadMore,
  renderOption,
  renderOptionPreview,
  renderValue,
  virtualized = true,
  menuPortalRoot,
  name,
  className,
  checkboxSize,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const controlSearchInputRef = useRef<HTMLInputElement>(null);
  const loadMoreLockedRef = useRef(false);
  const selectIdRef = useRef(
    `newui-select-${Math.random().toString(36).slice(2, 10)}`,
  );
  const outsideClickRefs = useMemo(() => [rootRef, menuRef], []);
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState("");
  const [scrollTop, setScrollTop] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hoveredOption, setHoveredOption] = useState<SelectOption | null>(null);
  const [menuStyles, setMenuStyles] = useState<CSSProperties>({});

  const allowMulti = isMulti ?? isMulty ?? false;
  const isEmbedded = menuBehavior === "embedded";
  const isMenuOpen = isEmbedded || isOpen;
  const isManual = selectionMode === "manual";
  const searchConfig = useMemo(
    () =>
      search?.enabled
        ? {
            mode: search.mode ?? "internal",
            placement: search.placement ?? "menu",
            placeholder: search.placeholder ?? "Search",
            ...search,
          }
        : null,
    [search],
  );
  const isControlSearch = searchConfig?.placement === "control";
  const menuSearchConfig = isControlSearch ? null : searchConfig;
  const searchValue =
    searchConfig?.mode === "external"
      ? searchConfig.value ?? ""
      : internalSearch;
  const selectedOptions = useMemo(() => getSelectedOptions(value), [value]);
  const [draftSelectedOptions, setDraftSelectedOptions] =
    useState<SelectOption[]>(selectedOptions);
  const currentSelectedOptions = isManual
    ? draftSelectedOptions
    : selectedOptions;
  const virtualConfig = getVirtualConfig(virtualized);
  const menuMaxHeight =
    dropMenuMaxHeight ??
    styleConfig?.maxMenuHeight ??
    SELECT_DEFAULT_MENU_HEIGHT;

  useEffect(() => {
    if (!isMenuOpen || !isManual) {
      setDraftSelectedOptions(selectedOptions);
    }
  }, [isManual, isMenuOpen, selectedOptions]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setScrollTop(0);
    setActiveIndex(-1);
    setHoveredOption(null);

    if (clearOnClose) {
      setInternalSearch("");
      search?.onChange?.("");
    }
  }, [clearOnClose, search]);

  useOutsideClick(outsideClickRefs, isOpen && !isEmbedded, closeMenu);

  useEffect(() => {
    if (!isMenuOpen || isEmbedded || dropMenuVariant !== "portal") {
      return undefined;
    }

    const updateMenuStyles = () => {
      const rootNode = rootRef.current;

      if (!rootNode) {
        return;
      }

      const rect = rootNode.getBoundingClientRect();
      const menuWidth =
        getCssSize(styleConfig?.menuWidth) ??
        getCssSize(styleConfig?.width) ??
        `${rect.width}px`;

      setMenuStyles({
        position: "fixed",
        top: rect.bottom + SELECT_MENU_OFFSET,
        left: rect.left,
        width: menuWidth,
        zIndex: 9999,
      });
    };

    updateMenuStyles();
    window.addEventListener("resize", updateMenuStyles);
    window.addEventListener("scroll", updateMenuStyles, true);

    return () => {
      window.removeEventListener("resize", updateMenuStyles);
      window.removeEventListener("scroll", updateMenuStyles, true);
    };
  }, [
    dropMenuVariant,
    isEmbedded,
    isMenuOpen,
    styleConfig?.menuWidth,
    styleConfig?.width,
  ]);

  const filteredOptions = useMemo(() => {
    if (
      !searchConfig ||
      searchConfig.mode === "external" ||
      !searchValue.trim()
    ) {
      return options;
    }

    const normalizedSearch = searchValue.trim().toLowerCase();

    return options.filter((option) => {
      const label = option.label.toLowerCase();
      const caption = option.caption?.toLowerCase() ?? "";

      return (
        label.includes(normalizedSearch) || caption.includes(normalizedSearch)
      );
    });
  }, [options, searchConfig, searchValue]);

  const visibleRange = useMemo(() => {
    if (!virtualConfig.enabled) {
      return {
        startIndex: 0,
        endIndex: filteredOptions.length,
        topPadding: 0,
        bottomPadding: 0,
      };
    }

    const visibleCount = Math.ceil(menuMaxHeight / virtualConfig.itemHeight);
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / virtualConfig.itemHeight) - virtualConfig.overscan,
    );
    const endIndex = Math.min(
      filteredOptions.length,
      startIndex + visibleCount + virtualConfig.overscan * 2,
    );

    return {
      startIndex,
      endIndex,
      topPadding: startIndex * virtualConfig.itemHeight,
      bottomPadding:
        Math.max(0, filteredOptions.length - endIndex) *
        virtualConfig.itemHeight,
    };
  }, [
    filteredOptions.length,
    menuMaxHeight,
    scrollTop,
    virtualConfig.enabled,
    virtualConfig.itemHeight,
    virtualConfig.overscan,
  ]);

  const visibleOptions = filteredOptions.slice(
    visibleRange.startIndex,
    visibleRange.endIndex,
  );
  const selectableFilteredOptions = useMemo(
    () => filteredOptions.filter((option) => !option.disabled),
    [filteredOptions],
  );
  const selectedOptionValueSet = useMemo(
    () => new Set(currentSelectedOptions.map((option) => option.value)),
    [currentSelectedOptions],
  );
  const isSelectAllChecked =
    selectableFilteredOptions.length > 0 &&
    selectableFilteredOptions.every((option) =>
      selectedOptionValueSet.has(option.value),
    );
  const isSelectAllDisabled =
    disabled || loading || selectableFilteredOptions.length === 0;
  const activeOption = activeIndex >= 0 ? filteredOptions[activeIndex] : null;
  const listboxId = `${selectIdRef.current}-listbox`;
  const activeOptionId = activeOption
    ? `${selectIdRef.current}-option-${activeIndex}`
    : undefined;

  const getNextEnabledIndex = useCallback(
    (startIndex: number, direction: 1 | -1): number => {
      if (filteredOptions.length === 0) {
        return -1;
      }

      for (let step = 0; step < filteredOptions.length; step += 1) {
        const nextIndex =
          (startIndex + direction * (step + 1) + filteredOptions.length) %
          filteredOptions.length;

        if (!filteredOptions[nextIndex].disabled) {
          return nextIndex;
        }
      }

      return -1;
    },
    [filteredOptions],
  );

  const getInitialActiveIndex = useCallback(() => {
    const selectedIndex = filteredOptions.findIndex(
      (option) =>
        !option.disabled &&
        currentSelectedOptions.some((selected) =>
          isSameOption(selected, option),
        ),
    );

    if (selectedIndex >= 0) {
      return selectedIndex;
    }

    return filteredOptions.findIndex((option) => !option.disabled);
  }, [currentSelectedOptions, filteredOptions]);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    setActiveIndex((previousIndex) =>
      previousIndex >= 0 ? previousIndex : getInitialActiveIndex(),
    );
  }, [getInitialActiveIndex]);

  useEffect(() => {
    if (!isOpen || !isControlSearch) {
      return;
    }

    controlSearchInputRef.current?.focus();
  }, [isControlSearch, isOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const nextActiveIndex =
      activeIndex >= 0 &&
      activeIndex < filteredOptions.length &&
      !filteredOptions[activeIndex].disabled
        ? activeIndex
        : getInitialActiveIndex();

    if (nextActiveIndex !== activeIndex) {
      setActiveIndex(nextActiveIndex);
    }
  }, [activeIndex, filteredOptions, getInitialActiveIndex, isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen || activeIndex < 0 || !virtualConfig.enabled) {
      return;
    }

    const listNode = listRef.current;

    if (!listNode) {
      return;
    }

    const optionTop = activeIndex * virtualConfig.itemHeight;
    const optionBottom = optionTop + virtualConfig.itemHeight;
    const listTop = listNode.scrollTop;
    const listBottom = listTop + listNode.clientHeight;

    if (optionTop < listTop) {
      listNode.scrollTop = optionTop;
      setScrollTop(optionTop);
    } else if (optionBottom > listBottom) {
      const nextScrollTop = optionBottom - listNode.clientHeight;
      listNode.scrollTop = nextScrollTop;
      setScrollTop(nextScrollTop);
    }
  }, [
    activeIndex,
    isMenuOpen,
    virtualConfig.enabled,
    virtualConfig.itemHeight,
  ]);

  useEffect(() => {
    if (!loadingMore) {
      loadMoreLockedRef.current = false;
    }
  }, [loadingMore, options.length]);

  const emitChange = useCallback(
    (nextSelectedOptions: SelectOption[]) => {
      if (allowMulti) {
        onChange?.(nextSelectedOptions);
        return;
      }

      onChange?.(nextSelectedOptions[0] ?? null);
    },
    [allowMulti, onChange],
  );

  const setSelection = useCallback(
    (nextSelectedOptions: SelectOption[]) => {
      if (isManual) {
        setDraftSelectedOptions(nextSelectedOptions);
      } else {
        emitChange(nextSelectedOptions);
      }
    },
    [emitChange, isManual],
  );

  const handleOptionClick = (option: SelectOption) => {
    if (option.disabled) {
      return;
    }

    const nextSelectedOptions = allowMulti
      ? getNextMultiValue(option, currentSelectedOptions)
      : [option];

    setSelection(nextSelectedOptions);

    if (!allowMulti && (closeOnSelect ?? true) && !isManual) {
      closeMenu();
    }
  };

  const toggleSelectAll = () => {
    if (!allowMulti || isSelectAllDisabled) {
      return;
    }

    if (isSelectAllChecked) {
      const filteredOptionValueSet = new Set(
        selectableFilteredOptions.map((option) => option.value),
      );

      setSelection(
        currentSelectedOptions.filter(
          (option) => !filteredOptionValueSet.has(option.value),
        ),
      );
      return;
    }

    const nextSelectedOptions = [...currentSelectedOptions];
    const nextSelectedValueSet = new Set(
      nextSelectedOptions.map((option) => option.value),
    );

    selectableFilteredOptions.forEach((option) => {
      if (!nextSelectedValueSet.has(option.value)) {
        nextSelectedOptions.push(option);
      }
    });

    setSelection(nextSelectedOptions);
  };

  const clearSelection = useCallback(() => {
    setSelection([]);
    if (!isManual) {
      footer?.onClear?.();
    }
  }, [footer, isManual, setSelection]);

  const doneSelection = useCallback(() => {
    if (isManual) {
      emitChange(draftSelectedOptions);
      footer?.onDone?.(
        allowMulti ? draftSelectedOptions : draftSelectedOptions[0] ?? null,
      );
    } else {
      footer?.onDone?.(
        allowMulti ? selectedOptions : selectedOptions[0] ?? null,
      );
    }

    closeMenu();
  }, [
    allowMulti,
    closeMenu,
    draftSelectedOptions,
    emitChange,
    footer,
    isManual,
    selectedOptions,
  ]);

  const handleSearchChange = (nextValue: string) => {
    setScrollTop(0);
    setActiveIndex(-1);

    if (searchConfig?.mode === "external") {
      searchConfig.onChange?.(nextValue);
      return;
    }

    setInternalSearch(nextValue);
    searchConfig?.onChange?.(nextValue);
  };

  const handleListScroll = (event: UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    setScrollTop(target.scrollTop);

    const scrollBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (
      hasMore &&
      !loadingMore &&
      !loadMoreLockedRef.current &&
      scrollBottom <=
        (typeof virtualized === "object"
          ? virtualized.threshold ?? SELECT_LOAD_MORE_THRESHOLD
          : SELECT_LOAD_MORE_THRESHOLD)
    ) {
      loadMoreLockedRef.current = true;
      onLoadMore?.();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();

      if (!isOpen) {
        openMenu();
        return;
      }

      setActiveIndex((previousIndex) =>
        getNextEnabledIndex(previousIndex < 0 ? -1 : previousIndex, 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      if (!isOpen) {
        openMenu();
        return;
      }

      setActiveIndex((previousIndex) =>
        getNextEnabledIndex(
          previousIndex < 0 ? filteredOptions.length : previousIndex,
          -1,
        ),
      );
      return;
    }

    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (event.key === "Enter" && isOpen) {
      event.preventDefault();

      if (activeOption) {
        handleOptionClick(activeOption);
      }
      return;
    }

    if (
      event.key === " " &&
      event.target instanceof HTMLElement &&
      event.target.tagName !== "INPUT"
    ) {
      event.preventDefault();

      if (!isOpen) {
        openMenu();
        return;
      }

      if (activeOption) {
        handleOptionClick(activeOption);
      }
    }
  };

  const removeValue = (option: SelectOption) => {
    const nextSelectedOptions = currentSelectedOptions.filter(
      (selected) => !isSameOption(selected, option),
    );

    if (isManual && !isMenuOpen) {
      emitChange(nextSelectedOptions);
      return;
    }

    setSelection(nextSelectedOptions);
  };

  const rootStyle: CSSProperties = {
    width: styleConfig?.fullWidth ? "100%" : getCssSize(styleConfig?.width),
    minWidth: getCssSize(styleConfig?.minWidth),
    maxWidth: getCssSize(styleConfig?.maxWidth),
  };

  const menuWidth = getCssSize(styleConfig?.menuWidth);
  const contentMenuStyle: CSSProperties = isEmbedded
    ? {
        width: menuWidth ?? "100%",
      }
    : dropMenuVariant === "content"
    ? {
        width:
          menuWidth ?? (styleConfig?.dynamicWidth ? "max-content" : "100%"),
      }
    : menuStyles;

  const menu = isMenuOpen ? (
    <SelectMenu
      menuRef={menuRef}
      listRef={listRef}
      menuClassName={styleConfig?.menuClassName}
      menuStyle={contentMenuStyle}
      listboxId={listboxId}
      optionIdPrefix={`${selectIdRef.current}-option`}
      isContentMenu={!isEmbedded && dropMenuVariant === "content"}
      isEmbedded={isEmbedded}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      createAction={createAction}
      closeMenu={closeMenu}
      searchConfig={menuSearchConfig}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      listHeader={listHeader}
      selectAll={selectAll ?? null}
      selectAllChecked={isSelectAllChecked}
      selectedCount={currentSelectedOptions.length}
      selectAllDisabled={isSelectAllDisabled}
      onSelectAllToggle={toggleSelectAll}
      menuMaxHeight={menuMaxHeight}
      onListScroll={handleListScroll}
      loading={loading}
      loadingMore={loadingMore}
      emptyLabel={emptyLabel}
      emptyState={emptyState}
      allowMulti={allowMulti}
      currentSelectedOptions={currentSelectedOptions}
      visibleOptions={visibleOptions}
      visibleStartIndex={visibleRange.startIndex}
      visibleRange={visibleRange}
      virtualConfig={virtualConfig}
      renderOption={renderOption}
      renderOptionPreview={renderOptionPreview}
      hoveredOption={hoveredOption}
      onOptionHover={setHoveredOption}
      onOptionClick={handleOptionClick}
      footer={footer}
      clearSelection={clearSelection}
      doneSelection={doneSelection}
      filteredOptionsCount={filteredOptions.length}
      checkboxSize={checkboxSize}
    />
  ) : null;

  return (
    <div
      ref={rootRef}
      className={classNames(
        "relative font-Manrope",
        styleConfig?.fullWidth && "w-full",
        styleConfig?.className,
        className,
      )}
      style={rootStyle}>
      {name && (
        <input
          type={"hidden"}
          name={name}
          value={currentSelectedOptions.map((option) => option.value).join(",")}
        />
      )}
      {!isEmbedded && (
        <div
          role={"combobox"}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup={"listbox"}
          aria-activedescendant={isOpen ? activeOptionId : undefined}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          className={classNames(
            "flex w-full items-center gap-2 rounded-lg border bg-white font-medium transition-all",
            SELECT_SIZE_CLASSES[size],
            error
              ? "border-[#DC2626] focus:border-[#DC2626]"
              : isOpen
              ? "border-[#6B4EFF] shadow-[0_0_0_1px_#6B4EFF]"
              : "border-[#DCE2EA] hover:border-[#6B4EFF]",
            disabled && "cursor-not-allowed bg-[#F8F9FB] text-[#9AA1AE]",
            styleConfig?.controlClassName,
          )}
          onKeyDown={handleKeyDown}
          onClick={() => {
            if (!disabled) {
              if (isOpen) {
                if (!isControlSearch) {
                  closeMenu();
                }
              } else {
                openMenu();
              }
            }
          }}>
          {icon && <span className={"shrink-0 text-[#6B7185]"}>{icon}</span>}
          {isControlSearch && isOpen ? (
            <Input
              ref={controlSearchInputRef}
              size={size}
              fullWidth={false}
              className={
                "min-h-0 min-w-0 flex-1 border-0 bg-transparent p-0 shadow-none hover:border-0 focus:border-0 focus:shadow-none"
              }
              inputClassName={"font-medium"}
              value={searchValue}
              placeholder={searchConfig?.placeholder ?? placeholder}
              disabled={disabled}
              onChange={(event) => handleSearchChange(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              onFocus={() => {
                if (!disabled) {
                  openMenu();
                }
              }}
            />
          ) : (
            <SelectValueView
              selectedOptions={currentSelectedOptions}
              isMulti={allowMulti}
              placeholder={placeholder}
              renderValue={renderValue}
              onRemove={removeValue}
            />
          )}
          {loading ? (
            <SelectSpinner className={"ml-auto shrink-0"} />
          ) : (
            <LuChevronDown
              size={16}
              className={classNames(
                "ml-auto shrink-0 text-[#9097A8] transition-transform",
                isOpen && "rotate-180",
              )}
            />
          )}
        </div>
      )}
      {!isEmbedded && dropMenuVariant === "portal" && menu
        ? createPortal(menu, menuPortalRoot ?? document.body)
        : menu}
    </div>
  );
};
